// src/components/Chat/MessageBubble.jsx
import React from "react";
import { FileIcon, DownloadIcon, Play, Pause, Clock as ClockIcon, XCircle as XCircleIcon, Flag, Check, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CONFIG } from "../../config";

// Helper
const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export default function MessageBubble({
  text = "",
  from = "guide",
  timestamp = null,
  type = "TEXT",
  meta = null,
  pending = false,
  status = null, // "sending", "sent", "failed"
  failed = false,
  id = null,
  senderId = null,
  onReport = null,
  isVent = false,
  isSecure = true
}) {
  const [isLetterOpen, setIsLetterOpen] = React.useState(false);

  // Use centralized config. 
  // API_HOST is the root (e.g. http://localhost:4000)
  const API_BASE = CONFIG.API_HOST;

  // Normalize status
  const isPending = pending || status === "sending";
  const isFailed = failed || status === "failed";

  // SYSTEM / STATUS MESSAGES
  if (from === "system" || type === "SYSTEM") {
    const upperText = (text || "").toUpperCase();
    if (upperText.includes("VERIFIED PROFESSIONAL") || upperText.includes("CERTIFIED PROFESSIONAL")) {
      return null;
    }

    // const isVerified = text.includes("VERIFIED PROFESSIONAL");
    const isSafety = text.includes("SAFETY NOTICE");

    // 🧘 Soften the display for uncertified providers
    let titleColor = "text-gray-500";
    let bgColor = "bg-gray-100";
    let iconColor = "text-gray-500";
    let icon = <ClockIcon className="w-4 h-4" />;

    if (isSafety) {
      titleColor = "text-amber-600";
      bgColor = "bg-amber-50";
      iconColor = "text-amber-500";
      icon = <AlertCircle className="w-4 h-4" />;
    }

    const lines = text.split("\n").filter(l => l.trim().length > 0);
    let title = lines.length > 1 ? lines[0] : (isSafety ? "Gentle Support Focus" : "System Message");
    
    // Final sanitization of the title
    if (title.includes("SAFETY NOTICE")) title = "Gentle Support Focus";

    const body = lines.length > 1 ? lines.slice(1).join(" ") : text;

    return (
      <div className="w-full flex justify-center my-6">
        <div className="max-w-[400px] flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md px-5 py-3.5 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
          <div className={`mt-0.5 p-1.5 rounded-full ${bgColor} ${iconColor}`}>
            {icon}
          </div>
          <div className="flex-1">
            <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${titleColor}`}>
              {title.replace(/\*/g, "")}
            </h4>
            <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
              {body.replace(/\*/g, "").replace("SAFETY NOTICE", "")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isUser = from === "user";
  const timeString = timestamp
    ? new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
    : "";

  const renderContent = () => {
    if (type === "IMAGE") {
      const src = text.startsWith("http") ? text : `${API_BASE}${text}`;
      return (
        <div className="rounded-lg overflow-hidden mt-1 mb-1 relative group/image">
          <img
            src={src}
            alt="Shared image"
            className={`max-w-full h-auto max-h-64 object-cover transition-opacity ${!isSecure ? 'blur-xl scale-110 pointer-events-none' : 'cursor-pointer hover:opacity-90'}`}
            onClick={() => isSecure && window.open(src, "_blank")}
          />
          {!isSecure && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-center p-3 pointer-events-none">
              <Flag className="w-8 h-8 text-white/70 mb-2" />
              <p className="text-white font-semibold text-xs leading-tight drop-shadow-md">This image has been flagged for sensitive content.</p>
            </div>
          )}
        </div>
      );
    }

    if (type === "FILE") {
      const href = text.startsWith("http") ? text : `${API_BASE}${text}`;
      const fileName = text.split("/").pop() || "Attached File";
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-3 bg-white/10 rounded-lg border border-white/20 mt-1 hover:bg-white/20 transition-colors"
        >
          <div className="p-2 bg-white/20 rounded-full">
            <FileIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{fileName}</div>
            <div className="text-xs opacity-70">Click to download</div>
          </div>
          <DownloadIcon className="w-4 h-4" />
        </a>
      );
    }

    if (type === "AUDIO") {
      const src = text.startsWith("http") ? text : `${API_BASE}${text}`;
      return <CustomAudioPlayer src={src} />;
    }

    if (type === "RESOURCE") {
      let resource = {};
      try {
        resource = JSON.parse(text);
      } catch (e) {
        return <div>{text}</div>; // Fallback
      }

      return (
        <div className="mt-1 mb-1 max-w-[280px] bg-white dark:bg-gray-700/50 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-600 shadow-sm transition-transform hover:scale-[1.02] cursor-pointer" onClick={() => window.open(`/article/${resource.id}`, "_blank")}>
          {resource.coverImage && (
            <div className="h-24 w-full bg-gray-200">
              <img src={resource.coverImage} className="w-full h-full object-cover" alt="" />
            </div>
          )}
          <div className="p-3">
            <h5 className="font-bold text-gray-900 dark:text-gray-100 text-sm line-clamp-2 leading-tight mb-1">
              {resource.title}
            </h5>
            <p className="text-xs text-gray-500 line-clamp-1 mb-2">
              by {resource.author}
            </p>
            <div className="flex items-center text-xs text-brand-blue font-medium">
              Read Guide <span className="ml-1">→</span>
            </div>
          </div>
        </div>
      );
    }

    const parseText = (content) => {
      // 1. Split by newlines
      return content.split("\n").map((line, i) => {
        // 2. Detect Article Links: /article/123
        const parts = line.split(/(\/article\/\d+|https?:\/\/[^\s]+|\*\*[^*]+\*\*)/g);

        return (
          <React.Fragment key={i}>
            {parts.map((part, j) => {
              if (part.match(/^\/article\/\d+$/)) {
                return (
                  <a key={j} href={part} className="underline font-semibold hover:opacity-80" target="_blank" rel="noopener noreferrer">
                    {part}
                  </a>
                );
              }
              if (part.match(/^https?:\/\//)) {
                return (
                  <a key={j} href={part} className="underline" target="_blank" rel="noopener noreferrer">
                    {part}
                  </a>
                );
              }
              if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={j}>{part.slice(2, -2)}</strong>;
              }
              return part;
            })}
            <br />
          </React.Fragment>
        );
      });
    };

    if (type === "LETTER") {
      let letterContent = text;
      let unlockAt = 0;
      try {
        const parsed = JSON.parse(text);
        if (parsed.text) {
          letterContent = parsed.text;
          unlockAt = parsed.unlockAt || 0;
        }
      } catch (e) {
        // Assume fallback plaintext
      }

      const isLocked = unlockAt > Date.now();
      const lockHours = Math.ceil((unlockAt - Date.now()) / (1000 * 60 * 60));

      return (
        <div className="flex flex-col items-center gap-2 p-2 min-w-[220px]">
          <div
            onClick={() => { if (!isLocked) setIsLetterOpen(!isLetterOpen); }}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isLocked ? "bg-black/10 dark:bg-black/30 opacity-70 cursor-not-allowed" : "bg-white/20 hover:bg-white/30 cursor-pointer hover:scale-105 shadow-sm"}`}
          >
            <span className="text-2xl">{isLocked ? "🔒" : isLetterOpen ? "📖" : "✉️"}</span>
          </div>
          <div className="text-center mt-1">
            <h4 className="font-serif font-medium text-lg leading-tight">Soul Letter</h4>
            {isLocked ? (
              <p className="text-xs text-red-200 font-semibold mt-1">
                Unlocks in ~{lockHours}h
              </p>
            ) : (
              <p className="text-xs italic opacity-80 cursor-pointer hover:underline mt-1" onClick={() => setIsLetterOpen(!isLetterOpen)}>
                {isLetterOpen ? "Tap to close" : "Tap to open"}
              </p>
            )}
          </div>

          <AnimatePresence>
            {isLetterOpen && !isLocked && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-3 text-sm leading-relaxed italic opacity-90 border-t border-black/10 dark:border-white/10 pt-4 w-full text-center overflow-hidden"
              >
                {parseText(letterContent)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return <div className={`text-sm leading-relaxed ${isVent ? 'opacity-60 italic font-light' : ''}`}>{parseText(text)}</div>;
  };

  return (
    <div
      className={`flex max-w-[85%] ${isUser ? "self-end justify-end" : "self-start justify-start"} ${isPending ? "opacity-70" : "opacity-100"} transition-opacity duration-300`}
    >
      <div
        className={`px-4 py-2 rounded-2xl text-sm leading-relaxed relative group ${isUser
          ? isVent ? "bg-indigo-400/80 text-white backdrop-blur-sm" : "bg-indigo-600 text-white"
          : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          }`}
      >
        {renderContent()}

        {meta?.url && (
          <a
            href={meta.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs mt-2 underline"
          >
            <FileIcon className="w-4 h-4" />
            {meta.name} ({formatFileSize(meta.size)})
            <DownloadIcon className="w-3 h-3" />
          </a>
        )}

        {/* Footer: Time + Status Icon + Report Action */}
        <div className={`text-[10px] mt-1 flex items-center justify-end gap-2 ${isUser ? "text-indigo-100" : "text-gray-400"}`}>
          {timeString}

          {/* Status Icons */}
          {isUser && (
            <>
              {isPending && <ClockIcon className="w-3 h-3 animate-pulse" />}
              {isFailed && <XCircleIcon className="w-3 h-3 text-red-300" />}
              {/* Anxiety-free read receipt: soft gray, subtly scaled */}
              {!isPending && !isFailed && (
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 0.7 }} transition={{ duration: 0.5 }}>
                  <Check className="w-3 h-3 text-gray-300 dark:text-gray-500" />
                </motion.div>
              )}
            </>
          )}

          {/* Report Action (Footer) */}
          {!isPending && !isFailed && onReport && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReport(id, senderId);
              }}
              className="ml-1 p-0.5 text-gray-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
              title="Report this message"
            >
              <Flag className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Internal Custom Audio Player Component
function CustomAudioPlayer({ src }) {
  const [playing, setPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const audioRef = React.useRef(null);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const onLoadedMetadata = () => {
      setLoading(false);
      if (audio.duration === Infinity) {
        // WebM Bug: Infinity duration. We can't fix it easily on frontend without decoding.
        // We treat it as 0 for now, or just show playback.
        setDuration(0);
      } else {
        setDuration(audio.duration);
      }
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
    };

    const onError = () => {
      // Silently handle broken/expired media URLs (e.g. old ngrok links)
      setError(true);
      setLoading(false);
    };

    const onCanPlay = () => setLoading(false);

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audio.addEventListener("canplay", onCanPlay);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("canplay", onCanPlay);
    };
  }, [src]);

  const togglePlay = () => {
    if (!audioRef.current || error) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(e => {
        console.error("Play failed", e);
        // toast.error("Playback failed"); // Optional
      });
    }
    setPlaying(!playing);
  };

  const formatTime = (time) => {
    if (!time || isNaN(time) || time === Infinity) return "0:00";
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };

  // Clean fallback for expired/unavailable media (e.g. old ngrok uploads)
  if (error) {
    return (
      <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg mt-1 text-white/60 text-xs italic">
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
        Audio unavailable
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 bg-white/20 p-2 rounded-lg min-w-[200px] mt-1">
      <audio ref={audioRef} src={src} preload="metadata" crossOrigin="anonymous" />

      <button
        onClick={togglePlay}
        className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-indigo-600 hover:scale-105 transition-transform"
        disabled={loading}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        ) : playing ? (
          <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
        ) : (
          <svg className="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
        )}
      </button>

      <div className="flex-1">
        <div className="h-1 bg-white/30 rounded-full overflow-hidden">
          <div className="h-full bg-white transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-white/80 mt-1 font-medium">
          <span>{formatTime(audioRef.current?.currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}
